import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import React, { useState } from "react";

import { CONVERSATION_LENS_PACK, CONVERSATION_LENSES } from "../../../../utils/terminology";
import { Lens, LensGroup } from "../../types";
import { LensDetailsDialog } from "./LensDetailsDialog";

interface LensGroupDialogProps {
  open: boolean;
  lensGroup: LensGroup | null;
  lenses: Lens[];
  onClose: () => void;
}

export const LensGroupDialog: React.FC<LensGroupDialogProps> = ({
  open,
  lensGroup,
  lenses,
  onClose
}) => {
  const [lensDetailsOpen, setLensDetailsOpen] = useState(false);
  const [selectedLens, setSelectedLens] = useState<Lens | null>(null);

  if (!lensGroup) {
    return null;
  }

  const groupLenses = lenses.filter(lens => lens.groupName === lensGroup.name);

  const handleViewLens = (lens: Lens) => {
    setSelectedLens(lens);
    setLensDetailsOpen(true);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pb: 2
          }}
        >
          {CONVERSATION_LENS_PACK}: {lensGroup.name}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {lensGroup.description && (
            <Box mb={2} mt={2}>
              <Typography variant="body1" color="text.secondary">
                {lensGroup.description}
              </Typography>
            </Box>
          )}

          <Typography variant="h6" gutterBottom sx={{ my: 2 }}>
            {CONVERSATION_LENSES}
          </Typography>

          {groupLenses.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">
                No lenses found in this group
              </Typography>
            </Box>
          ) : (
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <TableContainer>
                <Table>
                  <TableBody>
                    {groupLenses.map((lens, index) => (
                      <TableRow key={lens.id}>
                        <TableCell sx={{
                          py: 2,
                          border: 0,
                          borderBottom: index === groupLenses.length - 1 ? 0 : 1,
                          borderColor: "divider",
                          width: "100%"
                        }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {lens.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {lens.description || "No description provided"}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleViewLens(lens)}
                            >
                              View
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: "divider",
            pt: 2
          }}
        >
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <LensDetailsDialog
        open={lensDetailsOpen}
        lens={selectedLens}
        onClose={() => {
          setLensDetailsOpen(false);
          setSelectedLens(null);
        }}
      />
    </>
  );
};
